/// <reference types="vite/client" />
import '~/client/common/crypto-polyfill'
import '~/client/common/sentry-bootstrap'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '~/client/common/query-client'
import { HeadContent, Link, Outlet, Scripts, createRootRoute } from '@tanstack/react-router'
import * as React from 'react'
import { Toaster } from 'sonner'
import { DefaultCatchBoundary } from '~/client/components/DefaultCatchBoundary'
import { NotFound } from '~/client/components/NotFound'
import appCss from '~/styles/app.css?url'
import { seo } from '~/client/common/seo'
import { useAuthStore, useAdminUserDetail, useEffectiveUserDetail } from '~/client/common/stores/auth'
import { initPostHog, patchFetchWithPostHogHeaders, patchFetchWithImpersonationHeader } from '~/client/common/posthog'
import QuickAdminActionsDock from '~/client/components/admin/QuickAdminActionsDock'
import ImpersonationBanner from '~/client/components/admin/ImpersonationBanner'
import { initGleap, identifyGleapUser } from '~/client/common/gleap'
import Gleap from 'gleap'
import { startVersionCheck } from '~/client/common/version-check'
import { EditorialRecognitionDrawerHost } from '~/client/components/venue/EditorialRecognitionBadges'
import { FeatureFlag, hasFeature } from '~/client/common/features'

// Install impersonation fetch interceptor at module load — BEFORE the first
// TanStack Query (useEvents, useListings, etc.) fires its /api/** request.
// Inside useEffect/requestIdleCallback the patch races the first query and
// loses, leaking the admin's data into the target's view. The function is
// SSR-guarded and idempotent.
patchFetchWithImpersonationHeader()

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      ...seo({
        title: 'Revel Street',
        description: `Revel Street is a platform for finding and booking spaces for events. `,
      }),
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
      {
        rel: 'stylesheet',
        href:
          'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@100;200;300;400;500;600;700&family=Inter:wght@100;200;300;400;500;600;700;800;900&family=Libre+Baskerville:wght@400;700&family=Libre+Caslon+Text:wght@400;700&display=swap',
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      { rel: 'manifest', href: '/site.webmanifest', color: '#fffff' },
      { rel: 'icon', href: '/favicon.ico' },
    ],
  }),
  errorComponent: DefaultCatchBoundary,
  notFoundComponent: () => <NotFound />,
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const initializeAuth = useAuthStore((s) => s.initializeAuth)


  React.useEffect(() => {
    void initializeAuth()
    const envRole = import.meta.env.VITE_ENV_ROLE ?? 'preview'
    if (envRole !== 'production' && envRole !== 'preview') return

    // posthog.init synchronously appends recorder + exception-autocapture
    // <script> tags to the DOM. In React 19 concurrent hydration, even a
    // setTimeout(0) callback can fire mid-hydration, racing the reconciler
    // and producing a mismatch. Wait for the browser to be idle (or 1s
    // fallback) so hydration is definitively committed first.
    let cancelled = false
    const init = () => {
      if (cancelled) return
      void initPostHog()
      patchFetchWithPostHogHeaders()
    }
    type IdleApi = {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    const w = window as unknown as IdleApi
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(init, { timeout: 1000 })
      return () => { cancelled = true; w.cancelIdleCallback?.(id) }
    }
    const id = window.setTimeout(init, 200)
    return () => { cancelled = true; window.clearTimeout(id) }
  }, [initializeAuth])

  const adminUserDetail = useAdminUserDetail()
  const effectiveUserDetail = useEffectiveUserDetail()
  const hasEditorialRecognitionFiltersFeature = hasFeature(effectiveUserDetail, FeatureFlag.EDITORIAL_RECOGNITION_FILTERS)

  React.useEffect(() => {
    identifyGleapUser(adminUserDetail)
  }, [adminUserDetail])

  // Periodic version check — prompt reload when a new deploy is detected
  React.useEffect(() => startVersionCheck(), [])

  // Disable console.log in production
  if (import.meta.env.ENV_ROLE === 'production') {
    console.log = () => { }
  }

  const gtmId = import.meta.env.VITE_GTM_ID

  return (
    <html suppressHydrationWarning>
      <head>
        <HeadContent />
        {gtmId && (
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`,
            }}
          />
        )}
      </head>
      <body className="w-dvw h-dvh overflow-hidden rs-scrollbar-minimal" suppressHydrationWarning>
        {gtmId && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        )}
        <QueryClientProvider client={queryClient}>
          <ImpersonationBanner />
          <Outlet />
          {hasEditorialRecognitionFiltersFeature ? <EditorialRecognitionDrawerHost /> : null}
          {adminUserDetail?.is_superuser && <QuickAdminActionsDock />}
          <Toaster position="bottom-right" />
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  )
}
